import "../styles/globals.css";
import React, { useEffect, useRef, memo } from "react";
import App from "next/app";
import { ThemeProvider } from "@mui/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Head from "next/head";
import { Hydrate, QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import Layout from "../src/Layout";
import { Provider } from "react-redux";
import store from "../src/redux/store";
import theme from "../src/styles/theme";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "../src/styles/createEmotionCache";
import { PersistGate } from "redux-persist/integration/react";
import { CommonContextProvider } from "../components/helper/commonContext";
import { persistStore } from "redux-persist";
import { useRouter } from "next/router";
import mixpanel from "mixpanel-browser";
import TagManager from "react-gtm-module";

// console.log = () => {};
// console.error = () => {};
console.debug = () => {};

const persistor = persistStore(store, {}, function () {
  persistor.persist();
});

// const callGtagPerPage = (url) => gtag.pageView(url);

// const loadScript = (src) => {
//   return new Promise((resolve) => {
//     const script = document.createElement("script");
//     script.src = src;
//     script.onload = () => {
//       resolve(true);
//     };
//     script.onerror = () => {
//       resolve(false);
//     };
//     document.body.appendChild(script);
//   });
// };

function MyApp({ Component, pageProps }) {
  const router = useRouter();

  const ROUTES_TO_RETAIN = ["/nft/9-9-mashukan"];
  const retainedComponents = useRef({});
  const isRetainableRoute = ROUTES_TO_RETAIN.includes(router.asPath);

  // Add Component to retainedComponents if we haven't got it already
  if (isRetainableRoute && !retainedComponents.current[router.asPath]) {
    const MemoComponent = memo(Component);
    retainedComponents.current[router.asPath] = {
      component: <MemoComponent {...pageProps} />,
      scrollPos: 0,
    };
  }
  const handleRouteChangeStart = (url) => {
    if (isRetainableRoute) {
      retainedComponents.current[router.asPath].scrollPos = window.scrollY;
    }
  };

  // Save scroll position - requires an up-to-date router.asPath
  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChangeStart);
    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [router.asPath]);

  // Scroll to the saved position when we load a retained component
  useEffect(() => {
    if (isRetainableRoute) {
      window.scrollTo(0, retainedComponents.current[router.asPath].scrollPos);
    }
  }, [Component, pageProps]);

  // useEffect(() => {
  //   loadScript("https://d2q23p4t0ij9e0.cloudfront.net/fbpixel_9b8b26e0f1.js");
  // });

  // useEffect(() => {
  //   router.events.on("routeChangeComplete", callGtagPerPage);
  //   return () => {
  //     router.events.off("routeChangeComplete", callGtagPerPage);
  //   };
  // }, [router.events]);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { refetchOnWindowFocus: false, refetchOnMount: false },
    },
  });

  useEffect(() => {
    mixpanel.init("2b9c2e53f7cce84cb02d908352bc86cb", {
      debug: true,
      ignore_dnt: true,
    });
    mixpanel.track("App.js Page");

    const tagManagerArgs = {
      gtmId: "GTM-WL2TNZQ",
    };

    TagManager.initialize(tagManagerArgs);
  }, []);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Head>
      <QueryClientProvider client={queryClient}>
        <CommonContextProvider>
          <CacheProvider value={createEmotionCache()}>
            <Hydrate state={pageProps.dehydratedState}>
              <ReactQueryDevtools initialIsOpen={false} />
              <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                  {() => (
                    <ThemeProvider theme={theme}>
                      <CssBaseline />
                      <Layout {...pageProps}>
                        <div
                          style={{
                            display: isRetainableRoute ? "block" : "none",
                          }}
                        >
                          {Object.entries(retainedComponents.current).map(
                            ([path, c]) => (
                              <div
                                key={path}
                                style={{
                                  display:
                                    router.asPath === path ? "block" : "none",
                                }}
                              >
                                {c.component}
                              </div>
                            )
                          )}
                        </div>
                        {!isRetainableRoute && <Component {...pageProps} />}
                        {/* <Component {...pageProps} /> */}
                      </Layout>
                    </ThemeProvider>
                  )}
                </PersistGate>
              </Provider>
            </Hydrate>
          </CacheProvider>
        </CommonContextProvider>
      </QueryClientProvider>
    </>
  );
}
export default MyApp;

MyApp.getInitialProps = async (appContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  return { ...appProps };
};
